import { computed, ref, nextTick, unref, type Ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { toast } from "@mkbabb/glass-ui/toast";
import { useRouteTransition as useViewRouteTransition } from "@mkbabb/glass-ui/motion-core";
import type { SurveyWizardExposed } from "@src/types/survey.ts";
import { useVariantStore } from "@src/stores/useVariantStore.ts";
import { useApiStore } from "@src/stores/useApiStore.ts";
import { useSpeedtestStore } from "@features/speedtest";
import { useJourneyStore } from "@src/stores/useJourneyStore.ts";
import {
    routeToView,
    type AppView,
} from "@src/composables/useRouteTransition.ts";

interface AppNavigationOptions {
    surveyRef: Ref<
        (SurveyWizardExposed & { surveyRef?: SurveyWizardExposed }) | null
    >;
}

/**
 * useAppNavigation — bottom-dock router policy. The exported surface is
 * the six handlers the dock composition consumes; complete-state forward
 * routing folds into `onDockForward` so the dock has one forward handler.
 */
export function useAppNavigation(opts: AppNavigationOptions) {
    const { surveyRef } = opts;
    const variantStore = useVariantStore();
    const apiStore = useApiStore();
    const speedtest = useSpeedtestStore();
    const journey = useJourneyStore();

    const isCreatingSession = ref(false);
    const router = useRouter();
    const route = useRoute();

    // D14 / DDR-AV-ROUTE — the route-axis View-Transition helper (glass-ui
    // 3.10, SHIPPED). `navigate(go, { types })` wraps the navigation +
    // state mutation in the native View-Transition API with PRM + unsupported
    // fallbacks (information parity: the navigation ALWAYS runs, the morph is
    // conditional). The raw root is never touched — only this helper. Retake
    // rides `types:["retake"]` — the REVERSE direction of
    // the completion morph — so the complete-card → running-meter swap plays a
    // reverse view-transition rather than teleporting. ONE motion owner per
    // axis: this owns the route-URL/state-flip transition; the in-component
    // `ElementMorph` owns the meter→number geometric morph (the W2 shared
    // `transitionKey` keeps the route-level Vue `<Transition>` inert across
    // the running↔result flip, so there is no double-driving).
    const { navigate: navigateWithTransition } = useViewRouteTransition();

    const currentView = computed<AppView>(() => routeToView(route.name));

    function startSpeedtest(): void {
        speedtest.start();
    }

    /** SurveyView exposes the wizard interface (survey, editingFromReview,
     *  submitFromDock, returnToReview) directly via defineExpose. */
    function getWizardRef() {
        return surveyRef.value;
    }

    function goHome() {
        speedtest.reset();
        router.push({ name: "speedtest" });
    }

    function onDockBack() {
        const view = currentView.value;
        if (view === "speedtest") {
            goHome();
            return;
        }
        if (view === "survey") {
            const wizard = getWizardRef();
            if (unref(wizard?.editingFromReview)) {
                wizard.returnToReview();
                return;
            }
            const survey = wizard?.survey;
            if (survey && !unref(survey.isFirstStep)) {
                survey.prevStep();
            } else {
                router.push({ name: "speedtest" });
            }
        } else if (view === "thankyou") {
            router.push({ name: "survey" });
        } else if (view === "dashboard") {
            router.push({ name: "speedtest" });
        }
    }

    /**
     * Forward handler. Carries the old `onDockNext` post-test → survey
     * route behavior so PrimaryAction's complete-state path lands here.
     * Test-only variants (variant.surveyMode === "none") restart instead
     * of routing to the survey.
     */
    function onDockForward() {
        const view = currentView.value;
        if (view === "speedtest") {
            // Post-test path absorbed from the old onDockNext export.
            if (variantStore.variant.surveyMode === "none") {
                // Test-only variants restart rather than route to survey.
                speedtest.restart();
                return;
            }
            router.push({ name: "survey" });
            return;
        }
        if (view === "survey") {
            const wizard = getWizardRef();
            if (unref(wizard?.editingFromReview)) {
                wizard.returnToReview();
                return;
            }
            const survey = wizard?.survey;
            if (survey && !unref(survey.isLastStep)) {
                survey.nextStep();
            } else if (unref(survey?.isLastStep)) {
                wizard?.submitFromDock?.();
            }
            return;
        }
        if (view === "thankyou") {
            onDockRetake();
        }
    }

    /** Session-create failure surfaces a destructive toast so the operator
     *  sees the degraded path before the engine starts. The test still
     *  proceeds (result submission will degrade gracefully) — the toast is
     *  the affordance Norman's silent-dead-button violation needed.
     *
     *  The toast auto-dismisses after 6s (long enough to read the two lines,
     *  short enough to clear the surface on its own) via glass-ui's typed
     *  `duration` prop. Pairs with App.vue's top-anchored viewport override
     *  (F7 closed — no bottom-dock overlap). */
    function surfaceSessionCreateFailure(): void {
        toast({
            duration: 6000,
            title: "Couldn't reach the session service",
            description:
                "The test will run, but the result may not be saved. Try again in a moment.",
            variant: "destructive",
        });
    }

    async function onDockStart() {
        if (isCreatingSession.value) return;
        if (currentView.value !== "speedtest") {
            router.push({ name: "speedtest" });
        }
        isCreatingSession.value = true;
        try {
            await apiStore.createSession();
        } catch {
            surfaceSessionCreateFailure();
        } finally {
            isCreatingSession.value = false;
        }
        // A user-initiated start closes the auto-start latch so
        // subsequent remounts (e.g. survey → back-nav) don't auto-restart.
        // The journey store latch outlives per-mount composable state.
        journey.markAutoStarted();
        startSpeedtest();
    }

    function onDockStop() {
        speedtest.abort();
    }

    async function onDockRetake() {
        if (isCreatingSession.value) return;

        // D13/D14 + DDR-AV-ROUTE: retake is an EXPLICIT dock action (distinct
        // from the passive back-button, which never restarts). Re-arm the
        // auto-start gate BEFORE navigating — clearing the journey-store latch
        // lets the explicit start below own the fresh run cleanly, and the
        // running-route remount this navigation triggers re-evaluates the gate
        // against the live state rather than a stale "already auto-started"
        // latch from the prior run. Back-nav, by contrast, leaves the latch
        // set, so a passive return never auto-restarts.
        journey.clearAutoStartLatch();

        // D14 — the REVERSE view-transition (`types:["retake"]`). Wrap the
        // navigation back to the running route in the route-axis VT helper so
        // the complete-card → running-meter swap plays a reverse morph instead
        // of teleporting. The helper runs the navigation in BOTH the native and
        // the PRM / unsupported fallback paths (information parity — the route
        // always commits), and resolves `finished` once the swap settles. We
        // await `finished` so the post-navigate session work runs after the
        // transition has committed (no DOM poll — the helper's `finished`
        // promise is the sequencer). The state reset that actually flips
        // `data-engine-state` back to running is owned by `speedtest.start()`
        // below; the VT snapshots the route swap that frames it.
        const { finished } = navigateWithTransition(
            async () => {
                // Discard the `NavigationFailure | void` push result — the
                // helper's callback contract is `() => void | Promise<void>`,
                // and a navigation failure (a redirect / abort) is handled by
                // the router's own guards, not here.
                await router.push({ name: "speedtest" });
            },
            { types: ["retake"] },
        );
        await finished;
        await nextTick();

        isCreatingSession.value = true;
        try {
            await apiStore.createSession();
        } catch {
            surfaceSessionCreateFailure();
        } finally {
            isCreatingSession.value = false;
        }
        // Close the latch again now that the explicit retake start is firing,
        // so the speedtest-view remount the navigation triggered does not
        // double-fire its own auto-start alongside this one.
        journey.markAutoStarted();
        startSpeedtest();
    }

    return {
        onDockStart,
        onDockBack,
        onDockForward,
        onDockRetake,
        onDockStop,
    };
}
